import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { LeafMark } from '../Ornaments';
import './branch.css';

/** The welcome video plays only for guests who have not asked for less motion or data. */
export function canPlayHeroVideo(): boolean {
  try {
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return false;
    }
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    return !connection?.saveData;
  } catch {
    return false;
  }
}

interface HeroMediaProps {
  videoUrl?: string;
  imageUrl?: string;
  lang: 'gr' | 'en';
}

/**
 * Full-bleed backdrop for the picker and the branch welcome: video (muted, looped, inline,
 * poster = photo) → photo → brand-colour field. Decorative, so hidden from assistive tech;
 * a moving video gets a pause button (WCAG 2.2.2).
 */
const HeroMedia: React.FC<HeroMediaProps> = ({ videoUrl, imageUrl, lang }) => {
  const showVideo = !!videoUrl && canPlayHeroVideo();
  const [imageFailed, setImageFailed] = useState(false);
  const [paused, setPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const image = imageFailed ? undefined : imageUrl;

  // React sets `muted` as a property only; iOS wants the attribute before it autoplays.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.setAttribute('muted', '');
  }, [showVideo, videoUrl]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      Promise.resolve(video.play()).catch(() => {});
      setPaused(false);
    } else {
      video.pause();
      setPaused(true);
    }
  };

  return (
    <>
      <div className={`hero-media${!showVideo && !image ? ' hero-media--field' : ''}`} aria-hidden="true">
        {showVideo ? (
          <video
            ref={videoRef}
            className="hero-media__video"
            src={videoUrl}
            poster={image}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : image ? (
          <img className="hero-media__img" src={image} alt="" onError={() => setImageFailed(true)} />
        ) : (
          <LeafMark className="hero-media__mark" />
        )}
        <div className="hero-media__scrim" />
      </div>
      {showVideo && (
        <button
          type="button"
          className="hero-media__toggle"
          onClick={togglePlayback}
          aria-label={
            paused
              ? lang === 'en' ? 'Play video' : 'Αναπαραγωγή βίντεο'
              : lang === 'en' ? 'Pause video' : 'Παύση βίντεο'
          }
        >
          <FontAwesomeIcon icon={paused ? faPlay : faPause} />
        </button>
      )}
    </>
  );
};

export default HeroMedia;
